import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

import {
  Tab,
  Tabs,
  Box,
  Button,
  Typography,
  Card,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  LinearProgress,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Product,
  ProductFilterParams,
} from "../../../types/saleOrder/index";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import SaleOrderServices from "../../../services/SalesOrderService";
import CustomSelect from "../../component/common/customSelect";
import SearchField from "../../component/common/searchField";
import OrderHistory from "./OrderHistory";
import { SelectChangeEvent } from "@mui/material/Select";
import { showToast } from "../../component/common/toastNotification";
import { CURRENCY_SYMBOL } from "../../../constants/currency";
import { useTranslation } from "react-i18next";
import MakePaymentModal from "../invoice&payment/MakePaymentModal";
import { InvoiceType } from "../../../types/invoice&payment";
import { useSearchParams } from "react-router-dom";
import {
  getQuantityLimitMessage,
  QuantityLimitReason,
} from "../../../helper/quantityLimitToast";
import { useCart } from "../../../contexts/CartContext";
import CheckoutCartScreen from "./CheckoutCartScreen";
import { productToCartProduct } from "../../../utils/productToCartProduct";
import { refreshProductForAdd, runCheckoutGate } from "../../../utils/checkoutGate";
import { effectiveCap } from "../../../utils/cartPersistence";
import { formatCheckoutIqd, formatIqdMoney } from "../../../utils/formatCheckoutIqd";
import {
  consumeCheckoutUiEvent,
  onCheckoutUiEvent,
} from "../../../utils/checkoutEvents";
import { clearHeldCheckoutOrder, loadHeldCheckoutOrder } from "../../../utils/checkoutSession";
import { NJIM } from "../../../theme/njimTheme";


const mintProgressSx = {
  height: 6,
  borderRadius: 5,
  bgcolor: NJIM.muted,
  "& .MuiLinearProgress-bar": {
    borderRadius: 5,
    bgcolor: NJIM.mint,
  },
};

const primaryBtnSx = {
  background: `${NJIM.walletHero} !important`,
  color: `${NJIM.white} !important`,
  borderRadius: "999px",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 14px rgba(22,51,63,0.35) !important",
  "&:hover": {
    background: `${NJIM.walletHeroHover} !important`,
    boxShadow: "0 6px 18px rgba(22,51,63,0.45) !important",
  },
  "&.Mui-disabled": {
    background: "linear-gradient(118deg, rgba(22,51,63,0.45) 0%, rgba(29,67,86,0.45) 52%, rgba(20,42,53,0.45) 100%) !important",
    color: "rgba(255,255,255,0.55) !important",
    boxShadow: "none !important",
  },
} as const;

const SaleOrder: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");
  const fetchedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<number>(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [viewMode, setViewMode] = useState<"products" | "cart" | "review">("products");
  const [loading, setLoading] = useState<boolean>(true);
  const [gateLoading, setGateLoading] = useState(false);
  const { cart, addOrUpdateItem, removeItem, replaceItems, clearCart, getItemQuantity } =
    useCart();
  const [categoryData, setCategoryData] = useState<string[]>([]);
  const [openPaymentModal, setPaymentOpenModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceType | null>(
    null
  );
  const [filters, setFilters] = useState<ProductFilterParams>({
    keyword: "",
    category: "",
    pageNumber: 1,
    pageSize: 1000,
    sortDescending: false,
  });
  const [stockFilter, setStockFilter] = useState<"inStock" | "outOfStock">("inStock");
  const [quantityLimitByProductId, setQuantityLimitByProductId] = useState<
    Record<string, { reason: QuantityLimitReason; maxQty: number }>
  >({});

  // Open Order History when the URL asks for it (payment returns land on ?tab=OrderHistory).
  // Keyed on the tab value only: it used to re-run on every cart change, so after a QiCard/card
  // return any "add product" on New Order bounced the user back to Order History.
  useEffect(() => {
    if (tabParam === "OrderHistory") {
      setActiveTab(1);
    }
  }, [tabParam]);

  // Resume checkout when the URL carries ?checkout=<ts>. Runs once per checkout value — not on
  // every cart change, which would pull the user to the review step each time they add a product.
  const checkoutParam = searchParams.get("checkout");
  const cartCountRef = useRef(cart.items.length);
  cartCountRef.current = cart.items.length;
  useEffect(() => {
    if (checkoutParam) {
      setActiveTab(0);
      const hasCheckoutContext =
        cartCountRef.current > 0 || Boolean(loadHeldCheckoutOrder()?.orderId);
      if (hasCheckoutContext) {
        setActiveStep(1);
        setViewMode("review");
      } else {
        setActiveStep(0);
        setViewMode("products");
      }
    }
  }, [checkoutParam]);

  useEffect(() => {
    if (activeTab !== 0) return;
    if (activeStep === 1 && viewMode === "review" && cart.items.length === 0) {
      setActiveStep(0);
      setViewMode("products");
    }
  }, [activeTab, activeStep, viewMode, cart.items.length]);

  useEffect(() => {
    const apply = (event: ReturnType<typeof consumeCheckoutUiEvent>) => {
      if (!event) return;
      if (event.type === "success") {
        clearCart();
        clearHeldCheckoutOrder();
        setActiveStep(0);
        setViewMode("products");
        if (event.landOn === "history") setActiveTab(1);
        setPaymentOpenModal(false);
      }
      if (event.type === "card_pending") {
        clearCart();
        setActiveTab(1);
        setPaymentOpenModal(false);
      }
      if (event.type === "zaincash_failed" || event.type === "zaincash_cancelled") {
        setActiveStep(1);
        setViewMode("review");
        setPaymentOpenModal(true);
      }
      if (event.type === "keep_review") {
        setActiveStep(1);
        setViewMode("review");
        setPaymentOpenModal(true);
      }
    };
    apply(consumeCheckoutUiEvent());
    return onCheckoutUiEvent((event) => apply(event));
  }, [clearCart]);

  const debounce = <T extends unknown[]>(
    func: (...args: T) => void,
    delay: number
  ) => {
    let timer: NodeJS.Timeout;
    return (...args: T) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };
  const debouncedFetchProducts = React.useRef(
    debounce((params: ProductFilterParams) => {
      fetchProductList(params);
    }, 500)
  ).current;

  useEffect(() => {
    debouncedFetchProducts(filters);
  }, [filters, debouncedFetchProducts]);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchCategoryList();
      fetchedRef.current = true;
    }
  }, []);

  const handleSearchChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      keyword: value,
      pageNumber: 1,
    }));
  };

  const handleCategoryChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      category: value,
      pageNumber: 1,
    }));
  };

  const handleStockStatusToggle = (checked: boolean) => {
    setStockFilter(checked ? "inStock" : "outOfStock");
  };
  
  const fetchCategoryList = async () => {
    try {
      setLoading(true);
      const response = await SaleOrderServices.getCategoryList();
      const finalResponse = response?.data;
      if (finalResponse.length > 0) {
        setCategoryData(finalResponse);
      }
    } catch (error) {
      console.error("Error fetching Banner Image:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchProductList = async (filterParams?: ProductFilterParams) => {
    try {
      setLoading(true);

      const defaultParams: ProductFilterParams = {
        pageNumber: 1,
        pageSize: filters.pageSize,
        sortDescending: false,
        keyword: "",
        category: "",
        stockStatus: "",
        sortBy: "",
      };

      // Strip stockStatus — filtering is done client-side so all products load for every account
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { stockStatus: _ignored, ...mergedParams } = { ...defaultParams, ...filterParams };

      const response = await SaleOrderServices.getSaleProductList(mergedParams);
      const finalResponse = response?.data.items;

      if (finalResponse?.length > 0) {
        setProducts(finalResponse);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching Product List:", error);
    } finally {
      setLoading(false);
    }
  };

  const resolveMaxQty = useCallback(
    (productId: string, fallbackStock: number) => {
      const live = products.find((p) => String(p.id) === productId);
      const mapped = live ? productToCartProduct(live) : undefined;
      if (mapped) return effectiveCap(mapped);
      return fallbackStock;
    },
    [products]
  );

  const setLimitMessage = useCallback(
    (productId: string, maxQty: number, reason: QuantityLimitReason) => {
      setQuantityLimitByProductId((prev) => ({
        ...prev,
        [productId]: { reason, maxQty },
      }));
    },
    []
  );

  const clearLimitMessage = useCallback((productId: string) => {
    setQuantityLimitByProductId((prev) => {
      if (!prev[productId]) return prev;
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  }, []);

  const handleAddToCart = async (product: Product) => {
    const mapped = productToCartProduct(product);
    if (!Number.isFinite(mapped.price) || mapped.price <= 0) {
      showToast(
        t("salesOrder.invalidPricePopup", {
          defaultValue: "This product has IQD 0.00 price and cannot be ordered.",
        }),
        "error"
      );
      return;
    }

    let fresh = mapped;
    try {
      fresh = await refreshProductForAdd(mapped);
      setProducts((prev) =>
        prev.map((p) =>
          String(p.id) === fresh.id ? { ...p, stock: fresh.stock, price: fresh.price } : p
        )
      );
    } catch {
      fresh = mapped;
    }

    const cap = effectiveCap(fresh);
    if (cap < 1) {
      showToast(t("salesOrder.outOfStock"), "error");
      return;
    }

    const current = getItemQuantity(fresh.id);
    const nextQty = Math.min(current + 1, cap);
    if (nextQty === current) {
      setLimitMessage(String(product.id), cap, "at_limit");
      return;
    }
    if (nextQty !== current + 1) {
      setLimitMessage(String(product.id), cap, "capped");
    } else if (nextQty >= cap) {
      setLimitMessage(String(product.id), cap, "reached");
    } else {
      clearLimitMessage(String(product.id));
    }
    addOrUpdateItem(fresh, nextQty);
  };

  const handleRemoveItem = (productId: string) => {
    clearLimitMessage(productId);
    removeItem(productId);
  };

  const handleSetQuantity = (id: string, value: string, stock: number) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1) return;
    const line = cart.items.find((l) => l.product.id === id);
    if (!line) return;
    const maxQty = resolveMaxQty(id, stock);
    const capped = Math.min(parsed, maxQty);
    if (parsed > maxQty) {
      setLimitMessage(id, maxQty, "capped");
    } else if (parsed === maxQty) {
      setLimitMessage(id, maxQty, "reached");
    } else {
      clearLimitMessage(id);
    }
    addOrUpdateItem(line.product, capped);
  };

  const handleIncreaseQuantity = (id: string, stock: number) => {
    const line = cart.items.find((l) => l.product.id === id);
    if (!line) return;
    const maxQty = resolveMaxQty(id, stock);
    if (line.quantity >= maxQty) {
      setLimitMessage(id, maxQty, "at_limit");
      return;
    }
    const newQty = line.quantity + 1;
    if (newQty >= maxQty) setLimitMessage(id, maxQty, "reached");
    else clearLimitMessage(id);
    addOrUpdateItem(line.product, newQty);
  };

  const handleDecreaseQuantity = (id: string) => {
    const line = cart.items.find((l) => l.product.id === id);
    if (!line || line.quantity <= 1) return;
    const newQty = line.quantity - 1;
    const maxQty = resolveMaxQty(id, line.product.stock);
    if (newQty < maxQty) clearLimitMessage(id);
    addOrUpdateItem(line.product, newQty);
  };


  const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);
    // Keep ?tab= in sync with the tab the user picked, so a stale "OrderHistory" never lingers.
    const next = new URLSearchParams(searchParams);
    if (newTab === 1) next.set("tab", "OrderHistory");
    else next.delete("tab");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  };
  const steps = [
    t("salesOrder.selectProducts"),
    t("salesOrder.reviewConfirmOrder"),
  ];
  const isProductInCart = (productId: number) => {
    return getItemQuantity(String(productId)) > 0;
  };

  const announceGate = (gate: Awaited<ReturnType<typeof runCheckoutGate>>) => {
    if (gate.ok) return true;
    if (gate.reason === "invalid_price") {
      showToast(
        t("salesOrder.invalidPricePopup", {
          defaultValue: "This product has IQD 0.00 price and cannot be ordered.",
        }),
        "error"
      );
    } else if (gate.reason === "validate_failed") {
      showToast(
        t("salesOrder.unableToValidateStock", {
          defaultValue: "Unable to validate stock. Please try again.",
        }),
        "error"
      );
    } else if (gate.reason === "empty_after_update") {
      showToast(
        t("salesOrder.cartEmptyAfterStock", {
          defaultValue: "Cart is empty after stock update.",
        }),
        "info"
      );
    } else if (gate.reason === "changed") {
      showToast(
        t("salesOrder.cartChangedReview", {
          defaultValue: "Prices or stock changed. Review your cart.",
        }),
        "info"
      );
    }
    return false;
  };

  const handleContinueToCart = () => {
    if (cart.items.length === 0) return;
    setViewMode("cart");
    setActiveStep(0);
  };

  const handleContinueToReview = async () => {
    setGateLoading(true);
    try {
      const gate = await runCheckoutGate(cart.items, replaceItems);
      if (!announceGate(gate)) return;
      setActiveStep(1);
      setViewMode("review");
    } finally {
      setGateLoading(false);
    }
  };

  const handleBack = () => {
    if (viewMode === "review") {
      setActiveStep(0);
      setViewMode("cart");
      return;
    }
    if (viewMode === "cart") {
      setViewMode("products");
    }
  };

  const handleSubmitOrder = async () => {
    setGateLoading(true);
    try {
      const gate = await runCheckoutGate(cart.items, replaceItems);
      if (!announceGate(gate)) return;
      setSelectedInvoice({
        amount: cart.total,
      });
      setPaymentOpenModal(true);
    } finally {
      setGateLoading(false);
    }
  };

  // Stock and item-control limits come from the products API (site-scoped per customer).
  const displayedProducts = useMemo(() => {
    return products.filter((product) => {
      if (stockFilter === "outOfStock") {
        return product.stock <= 0;
      }
      return product.stock > 0;
    });
  }, [products, stockFilter]);

  const cartItemCount = cart.itemCount;
  const reviewLineSum = cart.items.reduce(
    (sum, line) => sum + Number(line.product.price) * line.quantity,
    0
  );
  const reviewVat = Math.max(0, cart.total - reviewLineSum);

  return (
    <>
      <Box className="dashboard-page" sx={{ direction: isRTL ? "rtl" : "ltr" }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: { xs: 22, sm: 26 },
              fontWeight: 700,
              color: NJIM.textPrimary,
              letterSpacing: "-0.02em",
            }}
          >
            {t("salesOrder.title")}
          </Typography>
          <Typography sx={{ color: NJIM.textSecondary, mt: 0.5, fontSize: 14 }}>
            {t("salesOrder.pageDescription")}
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: NJIM.muted,
            borderRadius: "12px",
            p: "5px",
            display: "inline-flex",
            width: { xs: "100%", md: "auto" },
            minWidth: { md: 320 },
            mb: 1.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Sale Order Tabs"
            sx={{
              width: "100%",
              minHeight: "36px",
              "& .MuiTabs-flexContainer": {
                gap: 0.5,
              },
              "& .MuiTab-root": {
                flex: 1,
                minHeight: 36,
                py: 1,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "14px",
                borderRadius: "10px",
                maxWidth: "none",
                color: NJIM.textSecondary,
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  bgcolor: NJIM.white,
                  color: NJIM.textPrimary,
                  boxShadow: NJIM.elevation,
                },
                "&:not(.Mui-selected):hover": {
                  bgcolor: "rgba(255,255,255,0.5)",
                },
              },
              ".MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab label={t("salesOrder.newOrder")} />
            <Tab label={t("salesOrder.orderHistory")} />
          </Tabs>
        </Box>

        <Card
          sx={{
            borderRadius: "12px",
            padding: { xs: 2, md: 3 },
            boxShadow: NJIM.elevation,
            border: "none",
            bgcolor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(20px) saturate(120%)",
            WebkitBackdropFilter: "blur(20px) saturate(120%)",
          }}
        >
          {/* New Order Tab Content */}
          {activeTab === 0 && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: "16px", fontWeight: 600, color: NJIM.textPrimary }} gutterBottom>
                  {t("salesOrder.createNewOrder")}
                </Typography>

                <Box
                  sx={{
                    border: `1px solid ${activeStep === 1 ? NJIM.mint : NJIM.border}`,
                    borderRadius: "999px",
                    bgcolor: activeStep === 1 ? NJIM.mintWash : NJIM.muted,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "11px",
                      padding: "5px 12px",
                      color: activeStep === 1 ? NJIM.ink : NJIM.textSecondary,
                    }}
                  >
                    {t("common.step")} {activeStep + 1} {t("common.of")}{" "}
                    {steps.length}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 1,
                  gap: 1,
                }}
              >
                <Box sx={{ width: "48%" }}>
                  <LinearProgress
                    variant="determinate"
                    value={activeStep >= 0 ? 100 : 0}
                    sx={mintProgressSx}
                  />
                </Box>
                <Box sx={{ width: "48%" }}>
                  <LinearProgress
                    variant="determinate"
                    value={activeStep >= 1 ? 100 : 0}
                    sx={mintProgressSx}
                  />
                </Box>
              </Box>

              {/* Step 1: products or cart (cart is not a stepper step) */}
              {activeStep === 0 && viewMode === "cart" && (
                <CheckoutCartScreen
                  onContinue={handleContinueToReview}
                  onBrowse={() => setViewMode("products")}
                  continueLoading={gateLoading}
                />
              )}
              {activeStep === 0 && viewMode !== "cart" && (
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    gap: 2.5,
                    flexDirection: {
                      xs: "column",
                      lg: "row",
                    },
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 600, color: NJIM.textPrimary, marginBottom: 0 }}
                      gutterBottom
                    >
                      {t("salesOrder.selectProducts")}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: NJIM.textSecondary,
                        marginBottom: 2,
                      }}
                      gutterBottom
                    >
                      {t("salesOrder.browseAndAdd")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        marginTop: 3,
                        gap: 2,
                      }}
                    >
                      <SearchField
                        value={filters.keyword}
                        onChange={handleSearchChange}
                        placeholder={t("salesOrder.searchProducts")}
                        sx={{
                          background: NJIM.muted,
                          width: { xs: "100%", sm: 220 },
                          flex: { xs: "1 1 100%", sm: "0 0 220px" },
                        }}
                      />
                      <Box
                        sx={{
                          width: {
                            xs: "100%",
                            sm: "70%",
                            md: "18%",
                          },
                        }}
                      >
                        <CustomSelect
                          name="categories"
                          placeHolder={t("salesOrder.allCategories")}
                          value={filters.category}
                          onChange={(
                            event: SelectChangeEvent<string | number>
                          ) =>
                            handleCategoryChange(event.target.value as string)
                          }
                          options={[
                            ...categoryData.map((category) => ({
                              label: category,
                              value: category,
                            })),
                          ]}
                          resettable
                        />
                      </Box>
                      <Box
                        sx={{
                          width: {
                            xs: "100%",
                            sm: "70%",
                            md: "22%",
                          },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          px: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={stockFilter === "inStock"}
                              onChange={(e) =>
                                handleStockStatusToggle(e.target.checked)
                              }
                              sx={{
                                "& .MuiSwitch-switchBase": {
                                  color: NJIM.textSecondary,
                                },
                                "& .MuiSwitch-track": {
                                  backgroundColor: NJIM.border,
                                  opacity: "1 !important",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: NJIM.ink,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: NJIM.ink,
                                  opacity: "1 !important",
                                },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                              <Typography sx={{ fontSize: "11px", color: NJIM.textSecondary, fontWeight: 500 }}>
                                {t("salesOrder.stockFilter", { defaultValue: "Stock" })}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: stockFilter === "inStock" ? NJIM.ink : NJIM.textSecondary,
                                }}
                              >
                                {stockFilter === "inStock"
                                  ? t("salesOrder.inStock")
                                  : t("salesOrder.outOfStock")}
                              </Typography>
                            </Box>
                          }
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </Box>
                    {loading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          padding: 5,
                        }}
                      >
                        <CircularProgress size={44} sx={{ color: NJIM.mint }} />
                      </Box>
                    ) : (
                      <Table
                        sx={{
                          direction: isRTL ? "rtl" : "ltr",
                          border: `1px solid ${NJIM.border}`,
                          borderRadius: "12px",
                          overflow: "hidden",
                          "& .MuiTableCell-root": {
                            padding: "12px 14px",
                            borderBottom: `1px solid ${NJIM.border}`,
                            fontSize: "13px",
                            textAlign: "start",
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow sx={{ bgcolor: NJIM.muted }}>
                            <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                              {t("salesOrder.productName")}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                              {t("salesOrder.category")}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                              {t("salesOrder.description")}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                              {t("common.price")}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                              {t("common.actions")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayedProducts.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={5}
                                sx={{ textAlign: "center !important", color: NJIM.textSecondary, padding: 4 }}
                              >
                                {t("common.noData")}
                              </TableCell>
                            </TableRow>
                          ) : (
                            displayedProducts.map((product) => (
                              <TableRow
                                key={product.id}
                                sx={{
                                  transition: "background 0.2s ease",
                                  "&:hover": { bgcolor: NJIM.muted },
                                }}
                              >
                                <TableCell sx={{ color: NJIM.textPrimary, fontWeight: 500 }}>{product.name}</TableCell>
                                <TableCell sx={{ color: NJIM.textSecondary }}>{product.category}</TableCell>
                                <TableCell sx={{ color: NJIM.textSecondary }}>{product.description}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography sx={{ color: product.stock === 0 ? NJIM.textSecondary : NJIM.textPrimary, fontWeight: 500, fontSize: "13px" }}>
                                      {CURRENCY_SYMBOL}
                                      {formatIqdMoney(product.price)}
                                    </Typography>
                                    {product.stock === 0 && (
                                      <Box
                                        sx={{
                                          px: 1,
                                          py: 0.2,
                                          borderRadius: "6px",
                                          background: "rgba(220,38,38,0.08)",
                                          border: "1px solid rgba(220,38,38,0.15)",
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          color: NJIM.danger,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {t("salesOrder.outOfStock") || "Out of Stock"}
                                      </Box>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="contained"
                                    disableElevation
                                    disableRipple
                                    color="inherit"
                                    disabled={isProductInCart(product.id) || product.stock === 0}
                                    onClick={() => handleAddToCart(product)}
                                    className={[
                                      "sale-order-add-btn",
                                      isProductInCart(product.id) ? "sale-order-add-btn--added" : "",
                                      !isProductInCart(product.id) && product.stock !== 0 ? "njim-primary-btn" : "",
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                    startIcon={
                                      isProductInCart(product.id) || product.stock === 0
                                        ? undefined
                                        : <AddIcon sx={{ fontSize: 16 }} />
                                    }
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: "12px",
                                      borderRadius: "8px",
                                      gap: "6px",
                                      ...(isProductInCart(product.id)
                                        ? {
                                            bgcolor: "rgba(22,163,74,0.1) !important",
                                            color: `${NJIM.success} !important`,
                                            boxShadow: "none !important",
                                          }
                                        : product.stock === 0
                                          ? {
                                              bgcolor: `${NJIM.muted} !important`,
                                              color: `${NJIM.textSecondary} !important`,
                                              boxShadow: "none !important",
                                              border: `1px solid ${NJIM.border}`,
                                            }
                                          : primaryBtnSx),
                                      "&.Mui-disabled": {
                                        opacity: 1,
                                      },
                                    }}
                                  >
                                    {isProductInCart(product.id)
                                      ? `✓ ${t("common.added")}`
                                      : t("common.add")}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </Box>

                  {/* Cart Sidebar */}
                  <Box
                    sx={{
                      width: { xs: "100%", lg: 340 },
                      flexShrink: 0,
                      position: { lg: "sticky" },
                      top: { lg: 16 },
                      alignSelf: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: NJIM.white,
                        borderRadius: "12px",
                        border: `1px solid ${NJIM.border}`,
                        boxShadow: NJIM.elevation,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: { lg: "calc(100vh - 120px)" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 2.5,
                          py: 2,
                          borderBottom: `1px solid ${NJIM.border}`,
                        }}
                      >
                        <ShoppingCartOutlinedIcon sx={{ color: NJIM.mint, fontSize: 22 }} />
                        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: NJIM.textPrimary, flex: 1 }}>
                          {t("salesOrder.yourCart")}
                        </Typography>
                        {cartItemCount > 0 && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: NJIM.textSecondary,
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                              direction: "ltr",
                            }}
                          >
                            ({cartItemCount})
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          flex: 1,
                          overflowY: "auto",
                          px: 2.5,
                          py: cart.items.length > 0 ? 2 : 4,
                        }}
                      >
                        {cart.items.length === 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              py: 3,
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: "50%",
                                bgcolor: NJIM.muted,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 0.5,
                              }}
                            >
                              <ShoppingBagOutlinedIcon sx={{ fontSize: 28, color: NJIM.textSecondary }} />
                            </Box>
                            <Typography sx={{ fontSize: "14px", color: NJIM.textSecondary, maxWidth: 220, lineHeight: 1.5 }}>
                              {t("salesOrder.cartEmpty")}
                            </Typography>
                          </Box>
                        ) : (
                          cart.items.map((line) => {
                            const productId = line.product.id;
                            const limitNotice = quantityLimitByProductId[productId];
                            return (
                            <Box key={line.product.id} sx={{ mb: 1, "&:last-child": { mb: 0 } }}>
                            <Box
                              sx={{
                                p: 1.25,
                                borderRadius: "12px",
                                border: limitNotice
                                  ? "1px solid rgba(245,158,11,0.45)"
                                  : `1px solid ${NJIM.border}`,
                                bgcolor: NJIM.white,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                "&:hover": { bgcolor: NJIM.muted },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  gap: 1.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: NJIM.textPrimary,
                                    lineHeight: 1.3,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {line.product.name}
                                </Typography>
                                <Typography
                                  sx={{
                                    flexShrink: 0,
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: NJIM.ink,
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                    direction: "ltr",
                                    unicodeBidi: "isolate",
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  }}
                                >
                                  {formatCheckoutIqd(line.product.price * line.quantity)}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    color: NJIM.textSecondary,
                                    direction: "ltr",
                                    unicodeBidi: "isolate",
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  }}
                                >
                                  {formatCheckoutIqd(line.product.price)}{" "}
                                  {t("salesOrder.each", { defaultValue: "each" })}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: NJIM.muted,
                                    borderRadius: "999px",
                                    p: 0.25,
                                    flexShrink: 0,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDecreaseQuantity(productId)}
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      "&:hover": { bgcolor: NJIM.white },
                                    }}
                                  >
                                    -
                                  </IconButton>
                                  <input
                                    type="number"
                                    min={1}
                                    max={line.product.stock}
                                    value={line.quantity}
                                    onChange={(e) => handleSetQuantity(productId, e.target.value, line.product.stock)}
                                    onBlur={(e) => {
                                      const val = parseInt(e.target.value, 10);
                                      if (isNaN(val) || val < 1) {
                                        handleSetQuantity(productId, "1", line.product.stock);
                                      }
                                    }}
                                    style={{
                                      width: 32,
                                      textAlign: "center",
                                      fontWeight: 600,
                                      fontSize: "13px",
                                      color: NJIM.textPrimary,
                                      background: "transparent",
                                      border: "none",
                                      outline: "none",
                                      MozAppearance: "textfield",
                                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleIncreaseQuantity(productId, line.product.stock)}
                                    disabled={line.quantity >= line.product.stock}
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      "&:hover": { bgcolor: NJIM.white },
                                    }}
                                  >
                                    +
                                  </IconButton>
                                </Box>
                              </Box>
                              <Button
                                type="button"
                                size="small"
                                fullWidth
                                variant="outlined"
                                disableRipple
                                onClick={() => handleRemoveItem(productId)}
                                startIcon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  borderRadius: "8px",
                                  py: 0.75,
                                  borderColor: "rgba(220,38,38,0.4)",
                                  color: NJIM.danger,
                                  bgcolor: "rgba(220,38,38,0.08)",
                                  "&:hover": {
                                    borderColor: NJIM.danger,
                                    bgcolor: "rgba(220,38,38,0.14)",
                                  },
                                }}
                              >
                                {t("common.remove")}
                              </Button>
                              {limitNotice && (
                                <Box
                                  sx={{
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: "8px",
                                    background: "rgba(245,158,11,0.1)",
                                    border: "1px solid rgba(245,158,11,0.25)",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: NJIM.warning,
                                      lineHeight: 1.45,
                                    }}
                                  >
                                    {getQuantityLimitMessage(
                                      t,
                                      line.product.name,
                                      limitNotice.maxQty,
                                      limitNotice.reason
                                    )}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            </Box>
                          );
                          })
                        )}
                      </Box>

                      <Box
                        sx={{
                          px: 2.5,
                          pt: 2,
                          pb: 2.5,
                          bgcolor: NJIM.white,
                          borderTop: `1px solid ${NJIM.border}`,
                        }}
                      >
                        {cartItemCount > 0 && (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.75,
                              }}
                            >
                              <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary }}>
                                {t("common.subtotal")}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: NJIM.textSecondary,
                                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                }}
                              >
                                {formatCheckoutIqd(cart.total)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: NJIM.textPrimary }}>
                                {t("common.total")}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "20px",
                                  fontWeight: 600,
                                  color: NJIM.mint,
                                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                }}
                              >
                                {formatCheckoutIqd(cart.total)}
                              </Typography>
                            </Box>
                          </>
                        )}
                        <Button
                          variant="contained"
                          disableElevation
                          disableRipple
                          color="inherit"
                          className="njim-primary-btn"
                          fullWidth
                          disabled={cart.items.length === 0}
                          onClick={handleContinueToCart}
                          sx={{
                            ...primaryBtnSx,
                            py: 1.35,
                            borderRadius: "999px",
                          }}
                        >
                          {t("salesOrder.continueToCart", { defaultValue: "Continue to Cart" })}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Step 2: Review */}
              {activeStep === 1 && (
                <Box mt={3}>
                  <Typography sx={{ fontSize: "16px", fontWeight: 600, color: NJIM.textPrimary }} gutterBottom>
                    {t("salesOrder.reviewConfirmOrder")}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "13px", color: NJIM.textSecondary, marginBottom: 2 }}
                    gutterBottom
                  >
                    {t("salesOrder.reviewBeforeSubmission")}
                  </Typography>

                  <Box
                    sx={{
                      border: `1px solid ${NJIM.border}`,
                      borderRadius: "12px",
                      p: 2.5,
                      bgcolor: NJIM.muted,
                    }}
                  >
                    <Typography sx={{ pb: 2, fontWeight: 600, fontSize: "15px", color: NJIM.textPrimary }}>
                      {t("salesOrder.orderSummary")}
                    </Typography>
                    <Box
                      sx={{
                        borderBottom: `1px solid ${NJIM.border}`,
                        pb: 1.5,
                        mb: 1.5,
                      }}
                    >
                      {cart.items.map((line) => (
                        <Box
                          key={line.product.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                            py: 0.5,
                          }}
                        >
                          <Typography sx={{ fontSize: "14px", color: NJIM.textSecondary }}>
                            {line.product.name} x {line.quantity}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: NJIM.textPrimary,
                              direction: "ltr",
                              unicodeBidi: "isolate",
                            }}
                          >
                            {formatCheckoutIqd(line.product.price * line.quantity)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box>
                      {reviewVat > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            pt: 0.5,
                            mb: 0.5,
                          }}
                        >
                          <Typography sx={{ fontSize: "14px", color: NJIM.textSecondary }}>
                            {t("salesOrder.vat", { defaultValue: "VAT" })}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: NJIM.textSecondary,
                              direction: "ltr",
                              unicodeBidi: "isolate",
                            }}
                          >
                            {formatCheckoutIqd(reviewVat)}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          pt: 0.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: "15px", color: NJIM.textPrimary }}>
                          {t("common.total")}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "15px",
                            color: NJIM.ink,
                            direction: "ltr",
                            unicodeBidi: "isolate",
                          }}
                        >
                          {formatCheckoutIqd(cart.total)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      className="njim-secondary-btn"
                      sx={{
                        width: "fit-content",
                        px: 3,
                      }}
                      onClick={handleBack}
                    >
                      {t("common.back")}
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      disableRipple
                      color="inherit"
                      className="njim-primary-btn"
                      sx={{
                        width: "fit-content",
                        ...primaryBtnSx,
                        borderRadius: "999px",
                        px: 3,
                      }}
                      disabled={gateLoading || cart.items.length === 0}
                      onClick={handleSubmitOrder}
                    >
                      {gateLoading ? (
                        <CircularProgress size={20} sx={{ color: NJIM.white }} />
                      ) : (
                        t("salesOrder.submitOrder")
                      )}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {/* Order History Tab Content */}
          {activeTab === 1 && <OrderHistory refreshKey={0} />}
        </Card>
      </Box>
      <MakePaymentModal
        open={openPaymentModal}
        onClose={() => setPaymentOpenModal(false)}
        invoiceData={selectedInvoice}
        checkout
      />
    </>
  );
};

export default SaleOrder;
